import styled from '@emotion/styled';
import { useSetAtom, useAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import Gap from 'src/components/Gap';
import ModalLayout, { MobileModalType, ModalContent, ModalHeader, ModalHeaderTitle as ModalTitleBase } from 'src/components/ModalLayout';
import RadioBase from 'src/components/Radio';
import SubscribePlanStatus from 'src/components/SubscribePlanStatus';
import SubscriptionModal from 'src/components/VividiOnly/Subscription';
import { useHasInventoryItem } from 'src/hooks/useInventoryItem';
import { useMatchFilterById } from 'src/hooks/useMatchQuery';
import { matchFilterSelectionsAtom } from 'src/stores/match/atoms';
import { showModalAtom } from 'src/stores/modal/atoms';
import type { MatchFilterSelection } from 'src/types/Match';
import { ModalNavButtonType } from 'src/types/Modal';
import { ModalType } from 'src/types/Modal';
import LocalePreference from './Preference';
const Radio = styled(RadioBase)`
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
const ModalTitle = styled(ModalTitleBase)`
  font-size: 22px;
  font-weight: 900;

  ${({
  theme
}) => theme.screenSize.desktop} {
    font-size: 20px;
    font-weight: 700;
  }
`;
const StyledPlanStatus = styled(SubscribePlanStatus)`
  padding: 12px 0;
`;
export interface LocalePreferenceModalProps {
  onDismiss: () => void;
}
const FilterOption = styled.label`
  cursor: pointer;
  padding: 12px 0px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const FilterOptionText = styled.div<{
  active: boolean;
}>`
  font-size: 16px;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content12};
  ${({
  active
}) => active && `
    font-weight: 700;
  `}
`;
const HiddenInput = styled.input`
  opacity: 0;
  position: absolute;
  left: 0;
  width: 0px;
  height: 0px;
`;
const filterId = 'STATE_CHOICE';
export default function LocalePreferenceModal({
  onDismiss
}: LocalePreferenceModalProps) {
  const {
    t
  } = useTranslation();
  const showModal = useSetAtom(showModalAtom);
  const matchFilter = useMatchFilterById(filterId);
  const [matchFilterSelections, setMatchFilterSelections] = useAtom(matchFilterSelectionsAtom);
  const localeMatchFilterSelection = matchFilterSelections.find(selection => selection.filterId === matchFilter?.filterId);
  const multiSelectOptionKeys = localeMatchFilterSelection?.multiSelectOptionKeys ?? [];
  const isSelectNone = multiSelectOptionKeys.length === 0;
  const hasSubscription = useHasInventoryItem('VIVIDI_UNLIMITED');
  const handleChange = (key: string) => {
    if (!hasSubscription) {
      showModal({
        key: ModalType.SUBSCRIPTION_POPUP,
        component: () => <SubscriptionModal referrer='localefilter' />
      });
      return;
    }
    const filterSelection = {
      filterId,
      // PREDEFINED_RANGE_SELECT, MULTI_SELECT 에서는 gemCost 를 보내지 않음.
      optionGemCost: 0,
      multiSelectOptionKeys: multiSelectOptionKeys.includes(key) ? multiSelectOptionKeys.filter(k => k !== key) : [...new Set([...multiSelectOptionKeys, key])]
    } satisfies MatchFilterSelection;
    setMatchFilterSelections(matchFilterSelections.filter(a => a.filterId !== filterId).concat(filterSelection.multiSelectOptionKeys.length ? [filterSelection] : []));
  };
  const handleChangeAnywhere = () => {
    setMatchFilterSelections(matchFilterSelections.filter(a => a.filterId !== filterId));
  };
  const active = isSelectNone;
  const handleDismiss = () => {
    onDismiss?.();
  };
  return <ModalLayout onBack={handleDismiss} mobileType={MobileModalType.BOTTOM_SHEET} isDim mobileNavButtonType={ModalNavButtonType.BACK} navButtonType={ModalNavButtonType.BACK} data-sentry-element="ModalLayout" data-sentry-component="LocalePreferenceModal" data-sentry-source-file="index.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx">
        <ModalTitle mobileType={MobileModalType.BOTTOM_SHEET} buttonType={ModalNavButtonType.BACK} data-sentry-element="ModalTitle" data-sentry-source-file="index.tsx">
          {t('vividi_filter_sheet_title_region')}
        </ModalTitle>
      </ModalHeader>

      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <FilterOption htmlFor='anywhere' data-sentry-element="FilterOption" data-sentry-source-file="index.tsx">
          <Radio active={active} size={24} data-sentry-element="Radio" data-sentry-source-file="index.tsx" />
          <Gap width={8} data-sentry-element="Gap" data-sentry-source-file="index.tsx" />
          <FilterOptionText active={isSelectNone} data-sentry-element="FilterOptionText" data-sentry-source-file="index.tsx">
            {t('vividi_genderfilter_region_selected_zero')}
          </FilterOptionText>
          <HiddenInput id='anywhere' type='radio' value='anywhere' name='anywhere' checked={isSelectNone} onChange={handleChangeAnywhere} data-sentry-element="HiddenInput" data-sentry-source-file="index.tsx" />
        </FilterOption>

        <StyledPlanStatus data-sentry-element="StyledPlanStatus" data-sentry-source-file="index.tsx">{t('vividi_genderfilter_pref_sheet_subtitle_region')}</StyledPlanStatus>
        {matchFilter != null && <LocalePreference matchFilter={matchFilter} values={multiSelectOptionKeys} onChange={handleChange} />}
      </ModalContent>
    </ModalLayout>;
}